/**
 * Pack the `audio_end` WebSocket payload that the tablet sends at end-
 * of-turn (#381).
 *
 * Pre-#381 the payload was just `{type:'audio_end', snr?:n}`. The
 * server treated every empty transcript identically — fired the
 * silence-ladder regardless of whether the user was actually quiet,
 * the mic was broken, or speech reached Deepgram but came back blank.
 * That is the architectural root cause of the #378 game-deafness
 * pattern.
 *
 * #381 extends the payload so the server can distinguish three cases
 * before deciding how to respond:
 *
 *   - vad_silence            user was genuinely quiet (mic OK,
 *                            VAD never crossed threshold) → silence
 *                            ladder is the right escalation
 *   - vad_speech             VAD detected speech but the audio
 *                            transcribed empty → re-prompt path
 *                            ("sorry, that didn't come through")
 *   - no_audio_frames        zero chunks arrived from MediaRecorder
 *                            → mechanical failure → re-prompt path
 *   - mic_error              chunks arrived but tiny (<2KB total)
 *                            suggests a degraded stream → re-prompt
 *   - max_duration           60s utterance cap fired (#381 Q4 — for
 *                            now the server treats this as a normal
 *                            turn; reason is included so a future
 *                            handler can branch on it without
 *                            another schema change)
 *   - ws_dropped             WS closed mid-turn — sent only by the
 *                            cleanup paths
 *
 * `vad_detected_speech`, `audio_bytes`, `chunks`,
 * `calibrated_threshold` and (when measured) `snr` ride along as
 * raw signals so the server logs can post-hoc explain why a turn
 * was empty without requiring client-console correlation.
 */


/**
 * Decide which `reason` applies given the per-turn signals.
 * Pure / no I/O; safe to call from anywhere. Order of precedence
 * matters — earlier wins:
 *
 *   1. Mechanical failures first (no frames / tiny audio).
 *   2. Then explicit mic_error from MediaRecorder.onerror.
 *   3. Then max_duration if the utterance hit the 60s cap.
 *   4. vad_speech if VAD did cross the threshold.
 *   5. Otherwise vad_silence.
 *
 * `inputs` shape:
 *   {
 *     audioBytes:        number of bytes total in the chunks
 *     chunkCount:        number of chunks captured
 *     vadDetectedSpeech: VAD ever flagged speech this turn?
 *     micError:          MediaRecorder.onerror fired?
 *     maxDurationHit:    did the duration cap stop the turn?
 *     wsDropped:         did the WS close before audio_end?
 *   }
 */
export function classifyEndReason(inputs) {
  const {
    audioBytes = 0,
    chunkCount = 0,
    vadDetectedSpeech = false,
    micError = false,
    maxDurationHit = false,
    wsDropped = false,
  } = inputs || {};

  if (wsDropped) return 'ws_dropped';
  if (micError) return 'mic_error';
  if (chunkCount === 0) return 'no_audio_frames';
  // Tiny audio (<2KB) — server's existing audio_too_small floor; the
  // client should also flag this as mechanical so the server doesn't
  // need to re-derive.
  if (audioBytes > 0 && audioBytes < 2000) return 'mic_error';
  if (maxDurationHit) return 'max_duration';
  if (vadDetectedSpeech) return 'vad_speech';
  return 'vad_silence';
}


/**
 * Build the full `audio_end` payload. Includes only the fields that
 * have meaningful values (older server bundles tolerate unknown
 * fields, but skipping nulls keeps the WS frames small).
 */
export function buildAudioEndPayload(inputs) {
  const {
    audioBytes = 0,
    chunkCount = 0,
    vadDetectedSpeech = false,
    micError = false,
    maxDurationHit = false,
    wsDropped = false,
    // #712 instrumentation — optional free-text label for the
    // CALL-SITE that triggered an early teardown (e.g.
    // 'killMic_ws_onclose_unexpected', 'softMuteMic_pre_speaking',
    // 'cleanup_doHangUp'). Echoed back to the server via
    // `ws_dropped_source` so the server log can correlate which
    // tablet-side code path tore the recording down. Only meaningful
    // when wsDropped=true; ignored otherwise.
    wsDroppedSource = null,
    calibratedThreshold = null,
    turnSnr = null,
  } = inputs || {};

  const reason = classifyEndReason({
    audioBytes, chunkCount, vadDetectedSpeech, micError, maxDurationHit, wsDropped,
  });

  const payload = {
    type: 'audio_end',
    reason,
    vad_detected_speech: !!vadDetectedSpeech,
    audio_bytes: audioBytes,
    chunks: chunkCount,
  };
  if (calibratedThreshold !== null && calibratedThreshold !== undefined) {
    payload.calibrated_threshold = calibratedThreshold;
  }
  if (turnSnr !== null && turnSnr !== undefined) {
    payload.snr = turnSnr;
  }
  if (wsDropped && wsDroppedSource) {
    payload.ws_dropped_source = wsDroppedSource;
  }
  return payload;
}
